export type UpsRelativeRangePreset =
  | 'TODAY'
  | 'YESTERDAY'
  | 'LAST_3_DAYS'
  | 'LAST_7_DAYS'
  | 'LAST_30_DAYS'
  | 'LAST_90_DAYS'
  | 'LAST_12_MONTHS'
  | 'CUSTOM';

export const RELATIVE_RANGE_PRESETS: readonly Exclude<
  UpsRelativeRangePreset,
  'CUSTOM'
>[] = [
  'TODAY',
  'YESTERDAY',
  'LAST_3_DAYS',
  'LAST_7_DAYS',
  'LAST_30_DAYS',
  'LAST_90_DAYS',
  'LAST_12_MONTHS',
];

export const RELATIVE_RANGE_PRESET_LABELS: Record<
  UpsRelativeRangePreset,
  string
> = {
  TODAY: 'Today',
  YESTERDAY: 'Yesterday',
  LAST_3_DAYS: 'Last 3 days',
  LAST_7_DAYS: 'Last 7 days',
  LAST_30_DAYS: 'Last 30 days',
  LAST_90_DAYS: 'Last 90 days',
  LAST_12_MONTHS: 'Last 12 months',
  CUSTOM: 'Custom range',
};

export type UpsDateRangePickerVariant = 'relative-presets' | '';

export function resolveDateRangePickerTemplate(
  variant: string | null | undefined
): 'default' | 'relative-presets' {
  return variant === 'relative-presets' ? 'relative-presets' : 'default';
}

export function labelRelativeRangePreset(
  preset: UpsRelativeRangePreset | null | undefined
): string | null {
  if (!preset || preset === 'CUSTOM') {
    return null;
  }
  return RELATIVE_RANGE_PRESET_LABELS[preset];
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

/** Rolling relative ranges anchored on `now` (local calendar). */
export function computeRelativeRangePreset(
  preset: Exclude<UpsRelativeRangePreset, 'CUSTOM'>,
  now: Date
): { start: Date; end: Date } {
  const todayStart = startOfDay(now);
  const todayEnd = endOfDay(now);

  switch (preset) {
    case 'TODAY':
      return { start: todayStart, end: todayEnd };
    case 'YESTERDAY': {
      const day = new Date(todayStart);
      day.setDate(day.getDate() - 1);
      return { start: day, end: endOfDay(day) };
    }
    case 'LAST_3_DAYS':
    case 'LAST_7_DAYS':
    case 'LAST_30_DAYS':
    case 'LAST_90_DAYS': {
      const days =
        preset === 'LAST_3_DAYS'
          ? 3
          : preset === 'LAST_7_DAYS'
            ? 7
            : preset === 'LAST_30_DAYS'
              ? 30
              : 90;
      const start = new Date(todayStart);
      start.setDate(start.getDate() - (days - 1));
      return { start, end: todayEnd };
    }
    case 'LAST_12_MONTHS': {
      const start = new Date(todayStart);
      start.setMonth(start.getMonth() - 12);
      return { start, end: todayEnd };
    }
    default: {
      const _exhaustive: never = preset;
      return _exhaustive;
    }
  }
}
